import { getMeta } from '../db/dexie.js';
import { traducir } from '../i18n/index.js';
import type { DatosAbiertos } from '../lib/catalogos-abiertos.js';
import type { FotosSugeridas } from '../lib/fotosSugeridas.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Distinguishes "the network is not there" from "the server said no" — they need different UI. */
export class OfflineError extends Error {
  constructor() {
    super('Sin conexión');
    this.name = 'OfflineError';
  }
}

/** How long a request to the server is waited for (`request`). */
const ESPERA_MAX_MS = 60_000;

async function request<T>(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');

  if (init.auth !== false) {
    const token = await getMeta('token');
    if (!token) throw new ApiError(401, traducir('error.dispositivoNoRegistrado'));
    headers.set('authorization', `Bearer ${token}`);
  }

  /*
   * No answer within a minute is no connection. Without a limit a request the network left hanging
   * kept the sync «in flight» until the phone's system gave up on the socket, many minutes later,
   * and every sync asked for meanwhile waited behind it: sales went nowhere with signal back.
   */
  const corte = new AbortController();
  const reloj = setTimeout(() => corte.abort(), ESPERA_MAX_MS);
  let res: Response;
  let body: unknown;
  try {
    try {
      res = await fetch(path, { ...init, headers, signal: corte.signal });
    } catch {
      throw new OfflineError();
    }
    body = await res.json().catch(() => null);
  } finally {
    clearTimeout(reloj);
  }
  if (corte.signal.aborted && body === null) throw new OfflineError();
  if (!res.ok) {
    const message =
      (body as { error?: string } | null)?.error ?? `Error ${res.status} en ${path}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

/**
 * Bytes rather than JSON, for photos. Same token, same two failure shapes as `request`, so the
 * caller can tell "no signal" from "the server said no" in exactly the same way.
 */
async function binario(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getMeta('token');
  if (!token) throw new ApiError(401, traducir('error.dispositivoNoRegistrado'));
  const headers = new Headers(init.headers);
  headers.set('authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(path, { ...init, headers });
  } catch {
    throw new OfflineError();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Error ${res.status} en ${path}`, body);
  }
  return res;
}

export interface PushResponse {
  aceptados: { id: string; serverSeq: number }[];
  rechazados: { id: string | null; reason: string }[];
  serverSeq: number;
  serverTime: string;
  relojDerivaMs?: number;
}

export interface PullResponse {
  eventos: unknown[];
  serverSeq: number;
  hayMas: boolean;
  serverTime: string;
  relojDerivaMs?: number;
}

export const api = {
  health: () =>
    request<{ ok: boolean; db: boolean; instalado?: boolean; instalacionAbierta?: boolean }>(
      '/api/health',
      { auth: false },
    ),

  /** The shop's owners, for picking whose PIN lets a new device join. Never a PIN hash. */
  usuarios: () =>
    request<{ usuarios: { id: string; nombre: string; rol: string }[] }>('/api/auth/usuarios', {
      auth: false,
    }),

  /**
   * A new device joining a shop that is already set up, on the owner's PIN. The server gives it
   * its two-letter code, which is why this is the one step that cannot happen offline.
   */
  registrarDispositivo: (data: {
    usuarioId: string;
    pin: string;
    nombre: string;
    tipo: 'mostrador' | 'telefono' | 'camion' | 'admin';
  }) =>
    request<{ dispositivoId: string; codigo: string; token: string }>('/api/auth/dispositivo', {
      method: 'POST',
      body: JSON.stringify(data),
      auth: false,
    }),

  /** Every device the shop registered, with when each last reached the server. */
  dispositivos: () =>
    request<{
      dispositivos: {
        id: string;
        codigo: string;
        nombre: string;
        tipo: string;
        activo: boolean;
        ultimoVistoEn: string | null;
        /** How far its clock was off at its last sync, ahead positive; null before it said. */
        relojDerivaMs: number | null;
      }[];
    }>('/api/estado/dispositivos'),

  bootstrap: (data: {
    setupToken: string;
    nombre: string;
    pin: string;
    dispositivoNombre: string;
  }) =>
    request<{ usuarioId: string; dispositivoId: string; codigo: string; token: string }>(
      '/api/auth/bootstrap',
      { method: 'POST', body: JSON.stringify(data), auth: false },
    ),

  /**
   * Development only: get this device onto the system, whatever state it is in.
   *
   * The server mounts /api/dev at all only when it is not production and has no SETUP_TOKEN,
   * so on a real deployment this 404s rather than being refused — there is nothing to refuse.
   */
  devAcceso: () =>
    request<{
      usuarioId: string;
      dispositivoId: string;
      codigo: string;
      token: string;
      pin: string;
      serverSeq: number;
      creado: boolean;
    }>('/api/dev/acceso', { method: 'POST', auth: false }),

  /** Development only: empty the server — log, projections and every device token. */
  devReiniciar: () => request<{ ok: true }>('/api/dev/reiniciar', { method: 'POST', auth: false }),

  login: (data: { usuarioId: string; pin: string }) =>
    request<{ usuarioId: string; nombre: string; rol: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
      auth: false,
    }),

  push: (eventos: unknown[]) =>
    request<PushResponse>('/api/sync/push', {
      method: 'POST',
      body: JSON.stringify({ eventos, clientTime: new Date().toISOString() }),
    }),

  /** What the open catalogues know about a code the shop does not have (`routes/codigos.ts`). */
  codigo: (codigo: string) => request<DatosAbiertos>(`/api/codigos/${encodeURIComponent(codigo)}`),

  /** That code's photo of the front, through the shop's server: a page may not load one from elsewhere. */
  fotoDeCodigo: async (codigo: string) => (await binario(`/api/codigos/${encodeURIComponent(codigo)}/foto`)).blob(),

  /** Photos from the internet for a product that has none, by its name and its codes (`routes/sugerencias.ts`). */
  fotosSugeridas: (nombre: string, codigos: readonly string[]) =>
    request<FotosSugeridas>(
      `/api/sugerencias/fotos?${new URLSearchParams([['nombre', nombre], ...codigos.map((c): [string, string] => ['codigo', c])])}`,
    ),

  /** One of them, its bytes through the shop's server as a code's photo's are. */
  fotoSugerida: async (id: string) => (await binario(`/api/sugerencias/fotos/${encodeURIComponent(id)}`)).blob(),

  pull: (desde: number, limite = 500) =>
    request<PullResponse>(
      `/api/sync/pull?desde=${desde}&limite=${limite}&clientTime=${encodeURIComponent(new Date().toISOString())}`,
    ),

  estado: () =>
    request<{
      dispositivo: { id: string; codigo: string; nombre: string | null };
      serverSeq: number;
      ultimoSyncSeq: number;
      atrasado: number;
      serverTime: string;
    }>('/api/sync/estado'),

  rechazos: (todos = false) =>
    request<{
      rechazos: {
        id: number;
        eventoId: string;
        tipo: string;
        motivo: string;
        dispositivoId: string | null;
        creadoEn: string;
        revisado: boolean;
      }[];
    }>(`/api/estado/rechazos?todos=${todos}`),

  marcarRevisado: (id: number) =>
    request<{ ok: true }>(`/api/estado/rechazos/${id}/revisado`, { method: 'POST' }),

  /** Idempotent: the name is the SHA-256 of the bytes, so sending it twice stores it once. */
  subirFoto: async (huella: string, datos: Blob): Promise<void> => {
    await binario(`/api/fotos/${huella}`, {
      method: 'PUT',
      headers: { 'content-type': datos.type || 'application/octet-stream' },
      body: datos,
    });
  },

  bajarFoto: async (huella: string): Promise<Blob> =>
    (await binario(`/api/fotos/${huella}`)).blob(),
};
